import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { OrganizationMembership } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface AcceptInvitationDialogProps {
  invitation: OrganizationMembership;
  onAccept: () => void;
  onDecline: () => void;
  isProcessing?: boolean;
}

/**
 * Dialog component for accepting or declining organization invitations
 */
export function AcceptInvitationDialog({
  invitation,
  onAccept,
  onDecline,
  isProcessing = false,
}: AcceptInvitationDialogProps) {
  const { t, language } = useLanguage();

  const formatExpirationDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      const locale = language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US';
      return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  const expirationDate = formatExpirationDate(invitation.invitationExpiresUtc);

  return (
    <Dialog open={true}>
      <DialogContent showCloseButton={false} className="sm:max-w-[500px]" onPointerDownOutside={(e) => { e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>{t.dialogs.acceptInvitation.title}</DialogTitle>
          <DialogDescription>
            {t.dialogs.acceptInvitation.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium">{t.dialogs.acceptInvitation.organization}</span>
              <span className="text-sm font-semibold">{invitation.orgName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">{t.dialogs.acceptInvitation.role}</span>
              <span className="text-sm">{t.organizationMembers.roles[invitation.role]}</span>
            </div>
          </div>

          {expirationDate && (
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                {t.dialogs.acceptInvitation.expiresOn.replace('{date}', expirationDate)}
              </AlertDescription>
            </Alert>
          )}

          <p className="text-sm text-muted-foreground">
            {t.dialogs.acceptInvitation.question}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onDecline}
            disabled={isProcessing}
          >
            {t.dialogs.acceptInvitation.decline}
          </Button>
          <LoadingButton
            type="button"
            onClick={onAccept}
            isLoading={isProcessing}
          >
            {t.dialogs.acceptInvitation.accept}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
